/**
 * PDF Generation Utilities
 * Generate downloadable PDF reports for calculator results
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable: {
      finalY: number;
    };
  }
}

interface HouseLoanPDFData {
  loanAmount: number;
  interestRate: number;
  loanTermYears: number;
  monthlyPayment: number;
  totalPayment: number;
  totalInterest: number;
  language: string;
}

interface PartialRepaymentPDFData {
  outstandingBalance: number;
  interestRate: number;
  remainingInstalments: number;
  currentInstalment: number;
  partialAmount: number;
  frequency: string;
  keepInstalmentAmount: boolean;
  newInstalment?: number;
  newInstalments?: number;
  totalSaved?: number;
  interestSaved?: number;
  language: string;
}

const formatCurrency = (amount: number): string => {
  return `HKD ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Generate PDF for House Loan Calculator
 */
export const generateHouseLoanPDF = (data: HouseLoanPDFData): void => {
  const isEnglish = data.language === 'en';
  const doc = new jsPDF();
  
  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('House Loan Calculator Report', 14, 20);
  
  // Subtitle with date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  const date = new Date().toLocaleDateString(isEnglish ? 'en-US' : 'zh-HK', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  doc.text(`${isEnglish ? 'Generated on' : '生成日期'}: ${date}`, 14, 28);
  
  // Reset text color
  doc.setTextColor(0);
  
  // Loan Details Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Loan Details', 14, 40);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const loanDetails = [
    ['Loan Amount', formatCurrency(data.loanAmount)],
    ['Annual Interest Rate', `${data.interestRate}%`],
    ['Loan Term', `${data.loanTermYears} years (${data.loanTermYears * 12} months)`]
  ];
  
  autoTable(doc, {
    startY: 45,
    head: [],
    body: loanDetails,
    theme: 'plain',
    styles: { fontSize: 11, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { cellWidth: 100 }
    }
  });
  
  // Payment Summary Section
  const startY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Summary', 14, startY);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const paymentSummary = [
    ['Monthly Payment', formatCurrency(data.monthlyPayment)],
    ['Total Payment (over term)', formatCurrency(data.totalPayment)],
    ['Total Interest Paid', formatCurrency(data.totalInterest)]
  ];
  
  autoTable(doc, {
    startY: startY + 5,
    head: [],
    body: paymentSummary,
    theme: 'plain',
    styles: { fontSize: 11, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { cellWidth: 100, fontStyle: 'bold' }
    },
    didParseCell: (data: any) => {
      // Highlight the monthly payment row
      if (data.row.index === 0 && data.column.index === 1) {
        data.cell.styles.textColor = [37, 99, 235]; // Blue color
      }
    }
  });
  
  // Key Insights Section
  const insightsY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Key Insights', 14, insightsY);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const interestPercentage = ((data.totalInterest / data.loanAmount) * 100).toFixed(1);
  const monthlyIncomeNeeded = (data.monthlyPayment / 0.5).toFixed(0);
  
  const insights = isEnglish ? [
    `You will pay ${formatCurrency(data.totalInterest)} in interest over ${data.loanTermYears} years.`,
    `That's ${interestPercentage}% of your original loan amount in interest.`,
    `To qualify, you typically need monthly income of at least ${formatCurrency(Number(monthlyIncomeNeeded))}.`,
    `Consider making extra payments to reduce total interest cost.`
  ] : [
    `You will pay ${formatCurrency(data.totalInterest)} in interest over ${data.loanTermYears} years.`,
    `That's ${interestPercentage}% of your original loan amount in interest.`,
    `To qualify, you typically need monthly income of at least ${formatCurrency(Number(monthlyIncomeNeeded))}.`,
    `Consider making extra payments to reduce total interest cost.`
  ];
  
  let yPos = insightsY + 7;
  insights.forEach(insight => {
    doc.text(insight, 14, yPos, { maxWidth: 180 });
    yPos += 7;
  });
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(
    'Generated by PayWise365.com - Free Mortgage Calculator for Hong Kong',
    14,
    280
  );
  
  doc.setTextColor(100);
  doc.text(
    'Disclaimer: This report is for reference only. Actual terms may vary by lender. Consult a licensed mortgage advisor.',
    14,
    285,
    { maxWidth: 180 }
  );
  
  // Save the PDF
  const fileName = `House_Loan_Report_${date.replace(/\s/g, '_').replace(/,/g, '')}.pdf`;
  doc.save(fileName);
};

/**
 * Generate PDF for Partial Repayment Calculator
 */
export const generatePartialRepaymentPDF = (data: PartialRepaymentPDFData): void => {
  const isEnglish = data.language === 'en';
  const doc = new jsPDF();
  
  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Partial Repayment Analysis Report', 14, 20);
  
  // Subtitle with date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  const date = new Date().toLocaleDateString(isEnglish ? 'en-US' : 'zh-HK', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  doc.text(`${isEnglish ? 'Generated on' : '生成日期'}: ${date}`, 14, 28);
  
  // Reset text color
  doc.setTextColor(0);
  
  // Current Loan Details
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Current Loan Details', 14, 40);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const currentDetails = [
    ['Outstanding Balance', formatCurrency(data.outstandingBalance)],
    ['Interest Rate', `${data.interestRate}%`],
    ['Current Instalment', formatCurrency(data.currentInstalment)],
    ['Remaining Instalments', data.remainingInstalments.toString()],
    ['Payment Frequency', data.frequency === 'monthly' ? 'Monthly' : 'Fortnightly']
  ];
  
  autoTable(doc, {
    startY: 45,
    head: [],
    body: currentDetails,
    theme: 'plain',
    styles: { fontSize: 11, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { cellWidth: 100 }
    }
  });
  
  // Partial Repayment Plan
  const planY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Partial Repayment Plan', 14, planY);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const repaymentPlan = [
    ['Partial Repayment Amount', formatCurrency(data.partialAmount)],
    [
      'Repayment Strategy', 
      data.keepInstalmentAmount 
        ? 'Keep instalment amount, reduce period'
        : 'Keep period, reduce instalment'
    ]
  ];
  
  autoTable(doc, {
    startY: planY + 5,
    head: [],
    body: repaymentPlan,
    theme: 'plain',
    styles: { fontSize: 11, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { cellWidth: 100 }
    }
  });
  
  // Results Comparison
  const resultsY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Results After Partial Repayment', 14, resultsY);
  
  const results = data.keepInstalmentAmount ? [
    ['New Instalment Amount', formatCurrency(data.currentInstalment)],
    ['New Remaining Instalments', data.newInstalments?.toString() || 'N/A'],
    ['Instalments Saved', (data.remainingInstalments - (data.newInstalments || 0)).toString()]
  ] : [
    ['New Instalment Amount', formatCurrency(data.newInstalment || 0)],
    ['Instalment Reduction', formatCurrency(data.currentInstalment - (data.newInstalment || 0))],
    ['Remaining Instalments', data.remainingInstalments.toString()]
  ];
  
  autoTable(doc, {
    startY: resultsY + 5,
    head: [],
    body: results,
    theme: 'plain',
    styles: { fontSize: 11, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { cellWidth: 100, fontStyle: 'bold' }
    }
  });
  
  // Savings Summary
  const savingsY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(16, 185, 129); // Green color
  doc.text('Your Savings', 14, savingsY);
  doc.setTextColor(0);
  
  const savings = [
    ['Total Interest Saved', formatCurrency(data.interestSaved || 0)],
    ['Total Amount Saved', formatCurrency(data.totalSaved || 0)]
  ];
  
  autoTable(doc, {
    startY: savingsY + 5,
    head: [],
    body: savings,
    theme: 'plain',
    styles: { fontSize: 12, cellPadding: 4, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 100, textColor: [16, 185, 129] }
    }
  });
  
  // Recommendation
  const recY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Recommendation', 14, recY);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const recommendation = `Making a partial repayment of ${formatCurrency(data.partialAmount)} can save you ${formatCurrency(data.interestSaved || 0)} in interest. This is a smart financial move if you have extra savings and the interest rate on your loan is higher than what you can earn from low-risk investments.`;
  
  doc.text(recommendation, 14, recY + 7, { maxWidth: 180 });
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(
    'Generated by PayWise365.com - Free Loan Calculator for Hong Kong',
    14,
    280
  );
  
  doc.setTextColor(100);
  doc.text(
    'Disclaimer: This report is for reference only. Consult a licensed financial advisor before making decisions.',
    14,
    285,
    { maxWidth: 180 }
  );
  
  // Save the PDF
  const fileName = `Partial_Repayment_Report_${date.replace(/\s/g, '_').replace(/,/g, '')}.pdf`;
  doc.save(fileName);
};

/**
 * Generate PDF for Payment Schedule
 */
export interface PaymentScheduleItem {
  payment_number: number;
  payment_amount: number;
  principal_component: number;
  interest_component: number;
  remaining_balance: number;
}

export interface PaymentSchedulePDFData {
  schedule: PaymentScheduleItem[];
  totalPayments: number;
  totalAmount: number;
  totalInterest: number;
  language: string;
}

export const generatePaymentSchedulePDF = (data: PaymentSchedulePDFData): void => {
  const doc = new jsPDF();
  
  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Future Payment Schedule', 14, 20);
  
  // Subtitle with date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  const date = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  doc.text(`Generated on: ${date}`, 14, 28);
  
  // Reset text color
  doc.setTextColor(0);
  
  // Summary Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Schedule Summary', 14, 40);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const summary = [
    ['Total Payments', data.totalPayments.toString()],
    ['Total Amount', formatCurrency(data.totalAmount)],
    ['Total Interest', formatCurrency(data.totalInterest)]
  ];
  
  autoTable(doc, {
    startY: 45,
    head: [],
    body: summary,
    theme: 'plain',
    styles: { fontSize: 11, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { cellWidth: 100, fontStyle: 'bold' }
    }
  });
  
  // Payment Schedule Table
  const tableY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Schedule', 14, tableY);
  
  // Prepare table data
  const tableData = data.schedule.map(item => [
    item.payment_number.toString(),
    formatCurrency(item.payment_amount),
    formatCurrency(item.principal_component),
    formatCurrency(item.interest_component),
    formatCurrency(item.remaining_balance)
  ]);
  
  autoTable(doc, {
    startY: tableY + 5,
    head: [['#', 'Payment', 'Principal', 'Interest', 'Balance']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [37, 99, 235],
      textColor: 255,
      fontSize: 10,
      fontStyle: 'bold',
      halign: 'center'
    },
    styles: { 
      fontSize: 9, 
      cellPadding: 3,
      overflow: 'linebreak'
    },
    columnStyles: {
      0: { cellWidth: 15, halign: 'center' },
      1: { cellWidth: 35, halign: 'right' },
      2: { cellWidth: 35, halign: 'right' },
      3: { cellWidth: 35, halign: 'right' },
      4: { cellWidth: 40, halign: 'right' }
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    margin: { top: 10, bottom: 30 },
    didDrawPage: () => {
      // Footer on each page
      const pageCount = doc.getNumberOfPages();
      const currentPage = doc.getCurrentPageInfo().pageNumber;
      
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        'Generated by PayWise365.com',
        14,
        doc.internal.pageSize.height - 10
      );
      
      doc.text(
        `Page ${currentPage} of ${pageCount}`,
        doc.internal.pageSize.width - 40,
        doc.internal.pageSize.height - 10
      );
    }
  });
  
  // Save the PDF
  const fileName = `Payment_Schedule_${date.replace(/\s/g, '_').replace(/,/g, '')}.pdf`;
  doc.save(fileName);
};
